/**
 * Lightweight Supabase REST (PostgREST) queries — uses plain `fetch` instead of
 * the @supabase/supabase-js SDK so the main bundle stays free of the ~55 KiB
 * client. The SDK is still used (lazily) by Auth / Admin / FormPages / ChatWidget,
 * which are all in their own route chunks.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

async function rest<T = any>(path: string): Promise<T> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: {
      apikey: SUPABASE_ANON,
      Authorization: `Bearer ${SUPABASE_ANON}`,
      Accept: "application/json",
    },
  });
  if (!res.ok) throw new Error(`Supabase REST ${res.status}: ${await res.text()}`);
  return res.json();
}

async function restOne<T = any>(path: string): Promise<T | null> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: {
      apikey: SUPABASE_ANON,
      Authorization: `Bearer ${SUPABASE_ANON}`,
      Accept: "application/vnd.pgrst.object+json",
    },
  });
  if (res.status === 406 || res.status === 404) return null;
  if (!res.ok) throw new Error(`Supabase REST ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function fetchServices() {
  return rest(`services?is_active=eq.true&order=sort_order.asc&select=*`);
}

export async function fetchAllServicesAdmin() {
  // Admin-only — uses SDK with auth token via lazy path
  const { supabase } = await import("@/integrations/supabase/client");
  const { data, error } = await supabase
    .from("services")
    .select("*")
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data;
}

export async function fetchService(slug: string) {
  return restOne(`services?slug=eq.${encodeURIComponent(slug)}&select=*`);
}

export async function fetchPage(slug: string) {
  return restOne(`pages?slug=eq.${encodeURIComponent(slug)}&select=*`);
}

export async function fetchSettings() {
  const data = await rest<any[]>(`site_settings?select=*`);
  const map = new Map<string, Record<string, string | null>>();
  data?.forEach((row) =>
    map.set(row.key, {
      value_tr: row.value_tr,
      value_en: row.value_en,
      value_de: row.value_de,
      value_es: row.value_es,
      value_ru: row.value_ru,
      value_fr: row.value_fr,
    })
  );
  return map;
}
